"use client";

/**
 * Shows live logs newest first. The viewport owns fixed-height virtualization;
 * display preparation and filtering live in the task-log model and view hook.
 */
import { useEffect, useRef, useState } from "react";
import {
  ArrowUpIcon,
  CloseIcon,
  CopyIcon,
  DownloadIcon,
  PlayIcon,
  StopIcon,
} from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/Dialog";
import { TaskStatus, type Task as TaskInfo, type TaskLog } from "@/contracts/tasks";
import { useTaskLogView } from "../hooks/useTaskLogView";
import { useVirtualTaskLogs } from "../hooks/useVirtualTaskLogs";
import { createLogFilename, formatLogs } from "../model/task-log-view";
import { useTaskLogs } from "../data/task.queries";
import { TaskLogToolbar } from "./TaskLogToolbar";
import { TaskLogViewport } from "./TaskLogViewport";
import styles from "./TaskLogViewer.module.css";

const EMPTY_LOGS: readonly TaskLog[] = [];

type TaskActionCallbacks = {
  onSuccess?: () => void;
  onError?: () => void;
};

type TaskLogViewerProps = {
  task: TaskInfo;
  isActionPending: boolean;
  onRunTask: (taskId: string, callbacks?: TaskActionCallbacks) => void;
  onCancelTask: (taskId: string, callbacks?: TaskActionCallbacks) => void;
  onClose: () => void;
};

export function TaskLogViewer({
  task,
  isActionPending,
  onRunTask,
  onCancelTask,
  onClose,
}: TaskLogViewerProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const feedbackTimerRef = useRef<number | undefined>(undefined);
  const [isLive, setIsLive] = useState(true);
  const [actionFeedback, setActionFeedback] = useState<string>();
  const isTaskActive = task.status === TaskStatus.Running;
  const logsQuery = useTaskLogs(task, isLive);
  const logs = logsQuery.data ?? EMPTY_LOGS;
  const logView = useTaskLogView(logs);
  const virtualLogs = useVirtualTaskLogs(logView.displayRows, logs.length);
  const lastUpdatedAt = logsQuery.dataUpdatedAt ? new Date(logsQuery.dataUpdatedAt) : undefined;
  const TaskControlIcon = task.status === TaskStatus.Running ? StopIcon : PlayIcon;

  useEffect(
    () => () => {
      if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    },
    [],
  );

  function announce(message: string) {
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    setActionFeedback(message);
    feedbackTimerRef.current = window.setTimeout(() => setActionFeedback(undefined), 2000);
  }

  function handleQueryChange(value: string) {
    logView.setQuery(value);
    if (value && virtualLogs.isFollowing) virtualLogs.pauseFollowing();
  }

  async function copyView() {
    await navigator.clipboard.writeText(formatLogs(logView.filteredLogs));
    announce(`Copied ${logView.filteredLogs.length.toLocaleString()} log entries`);
  }

  function downloadLogs() {
    const blob = new Blob([formatLogs(logs)], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = createLogFilename(task.id);
    link.click();
    URL.revokeObjectURL(url);
    announce("Downloaded full log");
  }

  async function refreshLogs() {
    const result = await logsQuery.refresh();
    announce(result.isError ? "Logs could not be refreshed" : "Logs refreshed");
  }

  function toggleTask() {
    if (task.status === TaskStatus.Running) {
      onCancelTask(task.id, {
        onSuccess: () => announce("Task stopped"),
        onError: () => announce("Task could not be stopped"),
      });
      return;
    }

    onRunTask(task.id, {
      onSuccess: () => announce("Task started"),
      onError: () => announce("Task could not be started"),
    });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        size="viewport"
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus({ preventScroll: true });
        }}
      >
        <section className={styles.viewer} aria-label={`Logs for ${task.id}`}>
          <header className={styles.header}>
            <div className={styles.titleBlock}>
              <DialogTitle asChild>
                <h2 ref={titleRef} tabIndex={-1} title={task.id}>{task.id}</h2>
              </DialogTitle>
              <div className={styles.taskMeta}>
                <span className={styles.statusBadge} data-status={task.status.toLowerCase()}>
                  <span aria-hidden="true" />
                  {task.status}
                </span>
                <span>{logs.length.toLocaleString()} entries</span>
              </div>
            </div>
            <div className={styles.headerActions}>
              <Button
                className={styles.taskControlButton}
                variant={task.status === TaskStatus.Running ? "danger" : "primary"}
                size="sm"
                disabled={isActionPending}
                onClick={toggleTask}
                aria-label={`${task.status === TaskStatus.Running ? "Stop" : "Start"} ${task.id}`}
              >
                <TaskControlIcon />
                <span>
                  {isActionPending
                    ? task.status === TaskStatus.Running
                      ? "Stopping…"
                      : "Starting…"
                    : task.status === TaskStatus.Running
                      ? "Stop task"
                      : "Start task"}
                </span>
              </Button>
              <button
                type="button"
                disabled={logView.filteredLogs.length === 0}
                onClick={() => void copyView()}
                title="Copy the current filtered view"
              >
                <CopyIcon />
                <span>Copy view</span>
              </button>
              <button
                type="button"
                disabled={logs.length === 0}
                onClick={downloadLogs}
                title="Download every log entry"
              >
                <DownloadIcon />
                <span>Download</span>
              </button>
              <DialogClose asChild>
                <button className={styles.closeButton} type="button" aria-label="Close logs">
                  <CloseIcon />
                </button>
              </DialogClose>
            </div>
          </header>

          <TaskLogToolbar
            logCount={logs.length}
            isFetching={logsQuery.isFetching}
            isLive={isLive}
            isTaskActive={isTaskActive}
            level={logView.level}
            levelCounts={logView.levelCounts}
            onLevelChange={logView.setLevel}
            onLiveToggle={() => setIsLive((current) => !current)}
            onQueryChange={handleQueryChange}
            onRefresh={() => void refreshLogs()}
            query={logView.query}
          />

          <div className={styles.metaBar}>
            <span>
              <strong>{logView.filteredLogs.length.toLocaleString()}</strong>
              {logView.filteredLogs.length === logs.length
                ? " entries"
                : ` of ${logs.length.toLocaleString()} entries`}
            </span>
            <span className={styles.metaSpacer} />
            {actionFeedback ? (
              <span className={styles.feedback} role="status">
                {actionFeedback}
              </span>
            ) : null}
            <span>
              {lastUpdatedAt
                ? `Updated ${lastUpdatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : "Not updated"}
            </span>
            {isTaskActive && isLive ? (
              <span className={styles.receiving}>
                <span aria-hidden="true" />
                receiving
              </span>
            ) : null}
          </div>

          <TaskLogViewport
            logCount={logs.length}
            deferredQuery={logView.deferredQuery}
            displayRowCount={logView.displayRows.length}
            isError={logsQuery.isError}
            isPending={logsQuery.isPending}
            isTaskActive={isTaskActive}
            onScroll={virtualLogs.handleScroll}
            startIndex={virtualLogs.startIndex}
            totalHeight={virtualLogs.totalHeight}
            viewportRef={virtualLogs.viewportRef}
            visibleRows={virtualLogs.visibleRows}
          />

          {!virtualLogs.isFollowing && logView.displayRows.length > 0 ? (
            <button
              type="button"
              className={styles.followButton}
              onClick={virtualLogs.scrollToLatest}
            >
              <ArrowUpIcon />
              {virtualLogs.newLogCount > 0
                ? `${virtualLogs.newLogCount.toLocaleString()} new logs`
                : "Jump to latest"}
            </button>
          ) : null}
        </section>
      </DialogContent>
    </Dialog>
  );
}
